import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Row, Col, Button, Offcanvas } from 'react-bootstrap';
import { useSwipeable } from 'react-swipeable';
import { NavigationItem, isNavItemActive, shouldHideSubItems } from '../navigation/NavigationConfig';

interface BottomNavigationProps {
  navigationItems: NavigationItem[];
}

const BottomNavigation: React.FC<BottomNavigationProps> = ({ navigationItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [submenuItem, setSubmenuItem] = useState<NavigationItem | null>(null);
  
  // Create a copy of navigation items
  const modifiedNavigationItems = [...navigationItems];

  const workoutsIndex = modifiedNavigationItems.findIndex((item) => item.label === 'Workouts');
  const cardsIndex = modifiedNavigationItems.findIndex((item) => item.label === 'Cards');
  if (workoutsIndex >= 0 && cardsIndex >= 0) {
    const tmp = modifiedNavigationItems[workoutsIndex];
    modifiedNavigationItems[workoutsIndex] = modifiedNavigationItems[cardsIndex];
    modifiedNavigationItems[cardsIndex] = tmp;
  }
  
  // Select primary navigation items for the bottom bar (limit to 4 + More)
  const primaryNavItems = modifiedNavigationItems.slice(0, 4);
  const moreNavItems = modifiedNavigationItems.slice(4);

  // Swipe handlers for the more menu
  const swipeHandlers = useSwipeable({
    onSwipedDown: () => setShowMoreMenu(false),
    delta: 10,          // Min distance before swipe is registered (px)
    swipeDuration: 500, // Max time of swipe (ms)
    touchEventOptions: { passive: true }
  });

  // Use the shared isNavItemActive function
  const isActive = (item: NavigationItem) => {
    return isNavItemActive(item, location.pathname);
  };

  // Handle navigation with touch feedback
  const handleNavigation = (path: string) => {
    // Add touch ripple effect here if needed
    navigate(path);
  };

  const handlePrimaryItemClick = (item: NavigationItem) => {
    if (item.label === 'Cards' && item.subItems && item.subItems.length > 0) {
      setSubmenuItem(item);
      return;
    }
    handleNavigation(item.path);
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <div 
        className="d-md-none fixed-bottom bg-primary" 
        style={{ 
          boxShadow: '0px -2px 10px rgba(0,0,0,0.1)',
          zIndex: 1030
        }}
      >
        <Container fluid>
          <Row className="text-center py-2">
            {primaryNavItems.map(item => (
              <Col key={item.path} onClick={() => handlePrimaryItemClick(item)}>
                <div 
                  className={`d-flex flex-column align-items-center justify-content-center ${isActive(item) ? 'text-white' : 'text-light opacity-75'}`}
                  style={{ 
                    minHeight: '56px',  // Ensure touch target height
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '2px' }}>{item.icon}</div>
                  <div style={{ fontSize: '0.7rem', fontWeight: isActive(item) ? 'bold' : 'normal' }}>
                    {item.label}
                  </div>
                </div>
              </Col>
            ))}
            
            {/* More menu button */}
            <Col onClick={() => setShowMoreMenu(true)}>
              <div 
                className="d-flex flex-column align-items-center justify-content-center text-light opacity-75"
                style={{ 
                  minHeight: '56px',
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontSize: '1.5rem', marginBottom: '2px' }}>⋮</div>
                <div style={{ fontSize: '0.7rem' }}>More</div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        show={!!submenuItem}
        onHide={() => setSubmenuItem(null)}
        placement="bottom"
        className="d-md-none"
        style={{ height: 'auto', maxHeight: '80vh', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{submenuItem?.label}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-grid gap-2">
            {submenuItem && (
              <>
                <Button
                  variant="outline-primary"
                  className="w-100 text-start d-flex align-items-center py-3"
                  onClick={() => {
                    handleNavigation(submenuItem.path);
                    setSubmenuItem(null);
                  }}
                >
                  <span className="me-3" style={{ fontSize: '1.5rem' }}>{submenuItem.icon}</span>
                  <span>Overview</span>
                </Button>
                {submenuItem.subItems?.map((subItem) => (
                  <Button
                    key={subItem.path}
                    variant="outline-primary"
                    className="w-100 text-start d-flex align-items-center py-3"
                    onClick={() => {
                      handleNavigation(subItem.path);
                      setSubmenuItem(null);
                    }}
                  >
                    <span className="me-3" style={{ fontSize: '1.5rem' }}>{subItem.icon}</span>
                    <span>{subItem.label}</span>
                  </Button>
                ))}
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* More Menu Offcanvas */}
      <Offcanvas 
        show={showMoreMenu} 
        onHide={() => setShowMoreMenu(false)} 
        placement="bottom"
        className="d-md-none"
        style={{ height: 'auto', maxHeight: '80vh', borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}
        {...swipeHandlers}
      >
        <div className="text-center mt-2 mb-1">
          <div 
            style={{ 
              width: '50px', 
              height: '5px', 
              backgroundColor: '#dee2e6', 
              borderRadius: '5px',
              margin: '0 auto',
              cursor: 'grab'
            }} 
          />
        </div>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>More Options</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="d-grid gap-2">
            {moreNavItems.map(item => (
              <div key={item.path}>
                <Button
                  variant="outline-primary"
                  className="w-100 text-start d-flex align-items-center py-3"
                  onClick={() => {
                    handleNavigation(item.path);
                    setShowMoreMenu(false);
                  }}
                >
                  <span className="me-3" style={{ fontSize: '1.5rem' }}>{item.icon}</span>
                  <span>{item.label}</span>
                </Button>
                
                {/* Only show sub-items for certain navigation items */}
                {item.subItems && !shouldHideSubItems(item.label) && (
                  <div className="ms-4 mt-2 mb-3">
                    {item.subItems.map(subItem => (
                      <Button
                        key={subItem.path}
                        variant="light"
                        className="w-100 text-start d-flex align-items-center py-2 mb-2"
                        onClick={() => {
                          handleNavigation(subItem.path);
                          setShowMoreMenu(false);
                        }}
                      >
                        <span className="me-3">{subItem.icon}</span>
                        <span>{subItem.label}</span>
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default BottomNavigation;
